import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import uniq from "lodash-es/uniq";
import { useIntl } from "react-intl";

import {
  AnyEntity,
  EntitiesSupportingGroups,
  EntitiesSupportingRoutes,
  MapEntity,
} from "@mapmycustomers/shared/types/entity";
import { MapEntitiesSet } from "@mapmycustomers/shared/types/map";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Alert } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import AddRemoveButton from "@app/component/view/SelectBar/components/AddRemoveButton";
import BulkEditButton from "@app/component/view/SelectBar/components/BulkEditButton";
import DeleteButton from "@app/component/view/SelectBar/components/DeleteButton";
import EmailButton from "@app/component/view/SelectBar/components/EmailButton";
import { isDeleting } from "@app/component/view/SelectBar/store";
import { deleteEntities } from "@app/component/view/SelectBar/store/actions";
import MapMode from "@app/scene/map/enums/MapMode";
import {
  cleanLassoPaths,
  exitLassoMode,
  refreshLassoMode,
  restartSelection,
} from "@app/scene/map/store/actions";
import {
  getSelectedEntities,
  getSelectedRecords,
  getSelectedRecordsCount,
} from "@app/scene/map/store/mapMode/selectors";
import { getLassoedEntitiesState, getMapMode } from "@app/scene/map/store/selectors";
import { LASSO_SELECTIONS_LIMIT } from "@app/scene/map/utils/consts";
import { isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { mapEntityIdGetter } from "@app/util/map/idGetters";
import { mapEntityIdParser } from "@app/util/map/idParsers";

import GroupBulkOperations from "./GroupBulkOperations";
import styles from "./LassoBulkEdit.module.scss";
import messages from "./messages";
import Warnings from "./Warnings";

interface Props {
  cleanLassoPaths: typeof cleanLassoPaths;
  containsRestrictedDeleteItems: boolean;
  containsRestrictedEditItems: boolean;
  deleteEntities: typeof deleteEntities.request;
  deleting: boolean;
  exitLassoMode: typeof exitLassoMode;
  isReadOnlyMember: boolean;
  mode?: MapMode;
  noAccessToEntities: boolean;
  records: MapEntity[];
  refreshLassoMode: typeof refreshLassoMode;
  restartSelection: typeof restartSelection;
  selectedCount: number;
  selection: MapEntitiesSet;
}

const LassoBulkEdit: React.FC<Props> = ({
  cleanLassoPaths,
  containsRestrictedDeleteItems,
  containsRestrictedEditItems,
  deleteEntities,
  deleting,
  exitLassoMode,
  isReadOnlyMember,
  mode,
  noAccessToEntities,
  records,
  refreshLassoMode,
  restartSelection,
  selectedCount,
  selection,
}) => {
  const intl = useIntl();
  const analyticIssuer = useExtendedAnalytics(["Lasso", "Created Shape"], useAnalytics());

  const isLargeSelection = selectedCount > LASSO_SELECTIONS_LIMIT;
  const isGroupMode = mode === MapMode.GROUPS;

  const selectedEntities = useMemo(() => new Set(records), [records]);
  const selectedEntitiesCanBeEdited = useMemo(
    () => new Set(records.filter((entity) => entity.accessStatus?.update !== false)),
    [records]
  );

  const selectedEntitiesCannotBeDeleted = useMemo(
    () =>
      new Set(
        records.filter((entity) => entity.accessStatus?.delete === false).map(mapEntityIdGetter)
      ),
    [records]
  );

  const [selectionParsedEntityIds, selectionIds, entityTypes] = useMemo(() => {
    const selectionParsedEntityIds = Array.from(selection).map(mapEntityIdParser).filter(isDefined);
    const selectionIds = selectionParsedEntityIds.map((entity) => entity.id);
    const entityTypes = uniq(selectionParsedEntityIds.map((entity) => entity.entity));
    return [selectionParsedEntityIds, selectionIds, entityTypes];
  }, [selection]);

  const [deleteConfirmVisible, showDeleteConfirm, hideDeleteConfirm] = useBoolean(false);

  const handleShowDeleteConfirmation = useCallback(() => {
    analyticIssuer?.clicked(["Delete Records"], {
      entities: Array.from(selection),
    });
    showDeleteConfirm();
  }, [analyticIssuer, selection, showDeleteConfirm]);

  const handleReload = useCallback(() => {
    exitLassoMode();
  }, [exitLassoMode]);

  const handleDrawAgain = useCallback(() => {
    cleanLassoPaths();
    restartSelection();
  }, [cleanLassoPaths, restartSelection]);

  const handleDelete = useCallback(async () => {
    await Promise.all(
      entityTypes.map((entityType) => {
        const entityIds: Array<AnyEntity["id"]> = selectionParsedEntityIds
          .filter((entity) => {
            return (
              entity.entity === entityType &&
              !selectedEntitiesCannotBeDeleted.has(mapEntityIdGetter(entity as MapEntity))
            );
          })
          .map((entity) => entity.id);

        return deleteEntities({
          entityIds,
          entityType,
          multiEntities: entityTypes.length > 1 || entityIds.length > 1,
          onSuccess: () => {
            hideDeleteConfirm();
          },
        });
      })
    );

    handleReload();

    analyticIssuer?.clicked(["Delete Records", "Permanently Delete"], {
      entities: Array.from(selection),
    });
  }, [
    analyticIssuer,
    deleteEntities,
    entityTypes,
    handleReload,
    hideDeleteConfirm,
    selectedEntitiesCannotBeDeleted,
    selection,
    selectionParsedEntityIds,
  ]);

  const handleRefresh = useCallback(() => {
    refreshLassoMode();
  }, [refreshLassoMode]);

  const handleHideBulkEditModal = useCallback(
    (updated: boolean) => {
      if (updated) {
        handleRefresh();
      }
    },
    [handleRefresh]
  );

  const bulkEditDisabled = entityTypes.length > 1 || noAccessToEntities;

  return (
    <AnalyticsContext.Provider value={analyticIssuer}>
      <div className={styles.container}>
        {isLargeSelection ? (
          <div className={styles.alert}>
            <Alert
              action={
                <ButtonLink className={styles.drawAgain} onClick={handleDrawAgain}>
                  {intl.formatMessage(messages.drawAgain)}
                </ButtonLink>
              }
              message={intl.formatMessage(messages.largeSelection)}
              type="warning"
            />
          </div>
        ) : (
          <>
            <div className={styles.actions}>
              {isGroupMode && <GroupBulkOperations />}
              {entityTypes.length > 0 && (
                <>
                  <BulkEditButton
                    containsRestrictedEditItems={containsRestrictedEditItems}
                    disabled={bulkEditDisabled}
                    entityType={entityTypes[0]}
                    onHide={handleHideBulkEditModal}
                    selectedEntities={selectedEntitiesCanBeEdited}
                    tooltip={
                      noAccessToEntities
                        ? intl.formatMessage(messages.noAccessToEditWarning, {
                            b: (text) => <b>{text}</b>,
                          })
                        : bulkEditDisabled
                        ? intl.formatMessage(messages.severalEntityTypesWarning)
                        : undefined
                    }
                  />
                  <AddRemoveButton
                    disabled={isReadOnlyMember}
                    onReloadData={handleRefresh}
                    selectedGroupEntities={selectedEntities as Set<EntitiesSupportingGroups>}
                    selectedRouteEntities={selectedEntities as Set<EntitiesSupportingRoutes>}
                  />
                  <EmailButton
                    disabled={isReadOnlyMember}
                    isMapMode
                    selectedEntities={selectedEntities}
                  />
                </>
              )}
              <DeleteButton
                containsRestrictedDeleteItems={containsRestrictedDeleteItems}
                deleteConfirmationVisible={deleteConfirmVisible}
                deleting={deleting}
                disabled={noAccessToEntities}
                onDelete={handleDelete}
                onHideConfirmation={hideDeleteConfirm}
                onShowConfirmation={handleShowDeleteConfirmation}
                selectedEntityIds={selectionIds}
                selectedEntityTypes={entityTypes}
                tooltip={
                  noAccessToEntities
                    ? intl.formatMessage(messages.noAccessToDeleteWarning, {
                        b: (text) => <b>{text}</b>,
                      })
                    : undefined
                }
              />
              <Warnings />
            </div>
          </>
        )}
      </div>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  containsRestrictedDeleteItems: getLassoedEntitiesState(state).containsRestrictedDeleteItems,
  containsRestrictedEditItems: getLassoedEntitiesState(state).containsRestrictedEditItems,
  deleting: isDeleting(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
  mode: getMapMode(state),
  noAccessToEntities: getLassoedEntitiesState(state).noAccessToEntities,
  records: getSelectedEntities(state),
  selectedCount: getSelectedRecordsCount(state),
  selection: getSelectedRecords(state),
});

const mapDispatchToProps = {
  cleanLassoPaths,
  deleteEntities: deleteEntities.request,
  exitLassoMode,
  refreshLassoMode,
  restartSelection,
};

export default connect(mapStateToProps, mapDispatchToProps)(LassoBulkEdit);
